import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import NewsCard from "@/components/NewsCard";
import ShareButtons from "@/components/ShareButtons";
import CommentsSection from "@/components/CommentsSection";
import PollWidget from "@/components/PollWidget";
import AdSlot from "@/components/AdSlot";
import { api, formatDate, timeAgo } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { trackPageview, flushArticleDuration } from "@/lib/analytics";
import { Bookmark, ExternalLink, Clock, ChevronLeft } from "lucide-react";

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/articles/${slug}`).then((r) => {
      const data = r.data || {};
      const art = data.article || (data.slug ? data : null);
      setArticle(art);
      setRelated(Array.isArray(data.related) ? data.related : []);
      if (art?.slug) {
        trackPageview({ path: `/nota/${art.slug}`, articleSlug: art.slug, articleId: art.id });
      }
    }).catch((err) => {
      console.error("article fetch failed", slug, err?.response?.status, err?.message);
      setArticle(null);
    }).finally(() => setLoading(false));
    return () => { flushArticleDuration(); };
  }, [slug]);

  if (loading) {
    return (
      <>
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-12 space-y-6">
          <div className="h-6 bg-[color:var(--surface-2)] rounded animate-pulse w-32" />
          <div className="h-16 bg-[color:var(--surface-2)] rounded animate-pulse" />
          <div className="h-80 bg-[color:var(--surface-2)] rounded animate-pulse" />
        </main>
      </>
    );
  }
  if (!article) {
    return (
      <>
        <Header />
        <main className="max-w-3xl mx-auto px-4 py-24 text-center">
          <h1 className="font-headline text-3xl mb-4">Artículo no encontrado</h1>
          <Link to="/" className="accent-text">← Volver al inicio</Link>
        </main>
      </>
    );
  }

  // JSON-LD NewsArticle schema for SEO
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    "headline": article.title,
    "description": article.bajada,
    "image": article.featured_image ? [article.featured_image] : undefined,
    "datePublished": article.published_at,
    "dateModified": article.updated_at,
    "articleSection": article.category_slug,
    "author": { "@type": "Organization", "name": "POPU.news" },
    "publisher": {
      "@type": "Organization",
      "name": "POPU.news",
      "logo": { "@type": "ImageObject", "url": `${window.location.origin}/icons/icon-512.svg` }
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": `${window.location.origin}/nota/${article.slug}` }
  };

  return (
    <>
      <Header />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, "\\u003c") }}
      />
      <main className="max-w-[860px] mx-auto px-4 py-8 md:py-12" data-testid="article-page">
        <Link to={`/categoria/${article.category_slug}`} className="inline-flex items-center gap-1 text-sm font-semibold accent-text hover:underline mb-6" data-testid="article-back-category">
          <ChevronLeft size={14} /> {article.category_slug?.toUpperCase()}
        </Link>
        {article.volanta && (
          <p className="overline accent-text mb-3">{article.volanta}</p>
        )}
        <h1 className="font-headline uppercase text-3xl md:text-5xl leading-[1.05] tracking-tight mb-5" data-testid="article-title">
          {article.title}
        </h1>
        <p className="text-lg md:text-xl leading-relaxed mb-6" style={{ color: "var(--text-muted)" }}>
          {article.bajada}
        </p>
        <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-widest mb-8 pb-6 border-b" style={{ color: "var(--text-muted)", borderColor: "var(--border)" }}>
          <span>{formatDate(article.published_at)}</span>
          <span>·</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {article.read_minutes || 2} MIN DE LECTURA</span>
          <span>·</span>
          <span>{article.views} LECTURAS</span>
          <div className="ml-auto flex items-center gap-3">
            <button className="hover:accent-text" aria-label="Guardar"><Bookmark size={16} /></button>
          </div>
        </div>

        {article.featured_image && (
          <figure className="mb-8 -mx-4 md:mx-0">
            <img src={article.featured_image} alt={article.title} className="w-full aspect-video object-cover rounded-sm" />
          </figure>
        )}

        <AdSlot slot="article_header" category={article.category_slug} />

        <div className="article-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.body || "") }} />

        <AdSlot slot="article_footer" category={article.category_slug} />

        {article.source_url && (
          <div className="mt-8 p-5 rounded-sm border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
            <p className="overline mb-2" style={{ color: "var(--text-muted)" }}>Fuente original</p>
            <a
              href={article.source_url}
              target="_blank"
              rel="noreferrer"
              className="accent-text flex items-center gap-2 font-semibold"
            >
              {article.source_name || "Leer original"} <ExternalLink size={14} />
            </a>
          </div>
        )}

        {article.tags?.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="chip" style={{ cursor: "default" }}>#{t}</span>
            ))}
          </div>
        )}

        <div className="mt-10 pt-6 border-t flex flex-col md:flex-row md:items-center gap-4 justify-between" style={{ borderColor: "var(--border)" }}>
          <p className="overline" style={{ color: "var(--text-muted)" }}>COMPARTIR ESTA NOTA</p>
          <ShareButtons url={`/nota/${article.slug}`} title={article.title} />
        </div>

        <div className="mt-10">
          <PollWidget articleId={article.id} />
        </div>

        <CommentsSection articleId={article.id} />
      </main>

      {related.length > 0 && (
        <section className="max-w-[1440px] mx-auto px-4 md:px-8 py-12 border-t" style={{ borderColor: "var(--border)" }}>
          <h2 className="overline mb-6">NOTAS RELACIONADAS</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((a) => <NewsCard key={a.id} article={a} />)}
          </div>
        </section>
      )}

      <Footer />
    </>
  );
}
